import type { Localized } from '../types/notice'

/**
 * Demo figures for the elected officials' request dashboard.
 * Period figures cover the last 30 days (Sept. 1–30, 2026) and are compared
 * with the previous 30 days. Open requests are counted as of Sept. 30, 2026.
 * The same numbers feed the KPIs, ATNA and the "À votre attention" block.
 */
export interface IssueCount {
  label: Localized
  count: number
  previous: number
}

export interface DashboardStats {
  // Period (last 30 days)
  received: number
  receivedPrev: number
  onTime: number
  onTimePrev: number
  medianDays: number
  medianDaysPrev: number
  satisfaction: number
  satisfactionPrev: number
  surveyResponses: number
  /** Share of closed requests whose citizen answered the survey. */
  surveyRate: number
  issues: IssueCount[]
  // As of today
  open: number
  openAtRisk: number
  late: number
  /** Late requests 30 days ago, for comparison. */
  latePrev: number
}

export const DATA_AS_OF: Localized = {
  fr: 'Données à jour le 30 sept. 2026, 7 h',
  en: 'Data as of Sept. 30, 2026, 7 a.m.',
}

export const TODAY_LABEL: Localized = { fr: '30 sept. 2026', en: 'Sept. 30, 2026' }

/** Whole city — mayor, CAO and directors. */
export const CITY_STATS: DashboardStats = {
  received: 1284,
  receivedPrev: 1157,
  onTime: 94,
  onTimePrev: 91,
  medianDays: 2.6,
  medianDaysPrev: 3.1,
  satisfaction: 4.6,
  satisfactionPrev: 4.4,
  surveyResponses: 312,
  surveyRate: 25,
  issues: [
    { label: { fr: 'Éclairage public', en: 'Street lighting' }, count: 398, previous: 331 },
    { label: { fr: 'Nids-de-poule', en: 'Potholes' }, count: 308, previous: 297 },
    { label: { fr: 'Matières résiduelles', en: 'Waste collection' }, count: 244, previous: 236 },
    { label: { fr: 'Signalisation routière', en: 'Road signage' }, count: 128, previous: 121 },
    { label: { fr: 'Autres', en: 'Other' }, count: 206, previous: 172 },
  ],
  open: 49,
  openAtRisk: 2,
  late: 1,
  latePrev: 4,
}

/** Councillors' districts, keyed by district id. */
export const DISTRICT_STATS: Record<string, DashboardStats> = {
  d1: {
    received: 214,
    receivedPrev: 198,
    onTime: 95,
    onTimePrev: 92,
    medianDays: 2.4,
    medianDaysPrev: 2.9,
    satisfaction: 4.7,
    satisfactionPrev: 4.5,
    surveyResponses: 54,
    surveyRate: 26,
    issues: [
      { label: { fr: 'Éclairage public', en: 'Street lighting' }, count: 71, previous: 58 },
      { label: { fr: 'Nids-de-poule', en: 'Potholes' }, count: 56, previous: 54 },
      { label: { fr: 'Matières résiduelles', en: 'Waste collection' }, count: 41, previous: 43 },
      { label: { fr: 'Signalisation routière', en: 'Road signage' }, count: 19, previous: 18 },
      { label: { fr: 'Autres', en: 'Other' }, count: 27, previous: 25 },
    ],
    open: 8,
    openAtRisk: 0,
    late: 0,
    latePrev: 1,
  },
  d4: {
    received: 97,
    receivedPrev: 102,
    onTime: 96,
    onTimePrev: 95,
    medianDays: 2.3,
    medianDaysPrev: 2.4,
    satisfaction: 4.5,
    satisfactionPrev: 4.6,
    surveyResponses: 23,
    surveyRate: 24,
    issues: [
      { label: { fr: 'Éclairage public', en: 'Street lighting' }, count: 30, previous: 25 },
      { label: { fr: 'Nids-de-poule', en: 'Potholes' }, count: 22, previous: 28 },
      { label: { fr: 'Matières résiduelles', en: 'Waste collection' }, count: 19, previous: 20 },
      { label: { fr: 'Signalisation routière', en: 'Road signage' }, count: 9, previous: 11 },
      { label: { fr: 'Autres', en: 'Other' }, count: 17, previous: 18 },
    ],
    open: 4,
    openAtRisk: 0,
    late: 0,
    latePrev: 0,
  },
}

/** Pothole SLA compliance city-wide, the only service under the 80 % threshold. */
export const POTHOLE_ON_TIME = { current: 78, previous: 82, threshold: 80 }

/** The late / at-risk requests, all in sector B (districts 2 and 3). */
export const FLAGGED_REQUESTS = {
  late: { service: { fr: 'Nid-de-poule', en: 'Pothole' }, days: 11, sla: 7 },
  atRisk: { service: { fr: 'Réparation d’un bac brisé', en: 'Broken bin repair' }, days: 12, sla: 14, count: 2 },
  sector: { fr: 'secteur B (district 3)', en: 'sector B (district 3)' },
  districtIds: ['d2', 'd3'],
}
